// Identify a single-segment alias usage at a Monaco cursor and return the
// `AliasBinding` that declares it (e.g. cursor on `u` in `WHERE u.id = 1`
// finds the `u` declared by `FROM users u`).
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 7 (alias).
//
// Multi-part chains (`u.col`, `schema.tbl`) belong to column / table dispatch
// — alias dispatch only fires when the cursor sits on a bare 1-part identifier.
// CTE / pseudo / cross-apply / outer-apply / lateral / using / derived-table
// sources are all handled uniformly because the dispatch is source-agnostic;
// every alias the resolver emits comes back with a `range` pointing at the
// alias identifier in source, which is what F12 navigates to.
//
// Returns `{ binding, isOnDeclaration }`:
//   - `binding` is the resolver's `IdentifierBinding` of `kind: 'alias'`,
//     keeping the same shape Slices 5/6 produce so the dispatcher in
//     `definitionProvider.ts` can narrow uniformly across kinds.
//   - `isOnDeclaration` is true when the cursor offset is inside the alias's
//     own declaration range — F12 is then a no-op (already at the target).

import type { ParseOptions, SqlEngine } from '../../sql-grammar/model';
import {
  findDottedChainStart,
  parseDottedChain,
} from '../../sql-resolver/dotted/dottedParts';
import { buildParsedDocumentFromSource } from '../../sql-grammar/document';
import { computeAliasBindingsForRange } from '../../sql-resolver/scope/aliasScope';
import { foldIdentifier } from '../../sql-resolver/scope/identifiers';
import type { AliasBinding, ParsedBuffer } from '../../sql-resolver/types';
import type { DatabaseEngineId } from '@/core/engines';
import type { IdentifierBinding } from '../types';
import { databaseEngineIdToSqlEngine } from '../../workspace/visual-query-editor/edit/livePullSchemaSnapshot';
import { extendCursorToIdentEnd } from './cursorIdent';

export type AliasBindingHit = {
  readonly binding: Extract<IdentifierBinding, { kind: 'alias' }>;
  readonly isOnDeclaration: boolean;
};

export type FindAliasDeps = {
  readonly engineId: DatabaseEngineId;
};

export function findAliasBindingAtCursor(
  source: string,
  offset: number,
  deps: FindAliasDeps,
): AliasBindingHit | null {
  const sqlEngine = databaseEngineIdToSqlEngine(deps.engineId) ?? 'mssql';
  const options: ParseOptions = {
    engine: sqlEngine,
    identifierFolding: defaultFoldingForEngine(sqlEngine),
  };

  const cursorAtEnd = extendCursorToIdentEnd(source, offset);
  const chainStart = findDottedChainStart(source, cursorAtEnd, options);
  const chain = parseDottedChain(source, chainStart, cursorAtEnd, options);
  if (!chain || chain.parts.length !== 1) return null;
  const aliasPart = chain.parts[0];

  const bindings = computeBindings(source, offset, options);
  if (!bindings) return null;

  const folded = foldIdentifier(aliasPart.value, aliasPart.quoted, options);
  const match = bindings.find((b) => b.aliasFolded === folded);
  if (!match) return null;

  const isOnDeclaration =
    offset >= match.range.startOffset && offset <= match.range.endOffset;

  return {
    binding: { kind: 'alias', target: match, definedAt: match.range },
    isOnDeclaration,
  };
}

function computeBindings(
  source: string,
  cursorOffset: number,
  options: ParseOptions,
): readonly AliasBinding[] | null {
  let parsedDocument;
  try {
    parsedDocument = buildParsedDocumentFromSource(source, options);
  } catch {
    return null;
  }
  const stmt = parsedDocument.statements.find(
    (s) => cursorOffset >= s.range.startOffset && cursorOffset <= s.range.endOffset,
  );
  const start = stmt?.range.startOffset ?? 0;
  const end = stmt?.range.endOffset ?? source.length;
  const buffer = { parsedDocument } as unknown as ParsedBuffer;
  try {
    return computeAliasBindingsForRange(buffer, start, end);
  } catch {
    return null;
  }
}

function defaultFoldingForEngine(engine: SqlEngine): ParseOptions['identifierFolding'] {
  if (engine === 'postgres') return 'lower';
  if (engine === 'oracle') return 'upper';
  return 'case-insensitive';
}
